import type {
  AlertBannerProps,
  AlertSeverityTypes
} from 'syf-component-library/ui/patterns/AlertBanner/typings'
import type { SupportedLanguage } from 'helpers/getLanguageFromStorage'
import getApptimizeStringDictionary from './getApptimizeStringDictionary'

const MISSING_VALUE = 'null'

const SEVERITY_VALUES = new Set<AlertSeverityTypes>([
  'high',
  'medium',
  'rectified',
  'info',
  'offline'
])

const isMissing = (value: string | undefined): boolean =>
  /*
   * adding another check of value === '' is redundant here
   * because if for any key the value is empty,
   * apptimize delete that key, so we either get undefined or 'null' for that key
   */
  value === undefined || value === MISSING_VALUE

const isSeverity = (value: string): value is AlertSeverityTypes =>
  SEVERITY_VALUES.has(value as AlertSeverityTypes)

const isSafeUrl = (raw: string): boolean => {
  try {
    const url = new URL(raw)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

const localize = (
  values: Record<string, string>,
  key: string,
  language: SupportedLanguage,
  fallback: string
): string => {
  if (language === 'es') {
    const esKey = `${key}Es`
    const esValue = values[esKey]
    if (esValue !== undefined && esValue !== MISSING_VALUE) {
      return esValue
    }
  }
  const enKey = `${key}En`
  return isMissing(values[enKey]) ? fallback : values[enKey]
}

export const fetchBannerValues = (): Record<string, string> =>
  getApptimizeStringDictionary('disasterAlertBannerValues', {})

const getDisasterReliefBannerProps = (
  bannerValues: Record<string, string>,
  language: SupportedLanguage
): (AlertBannerProps & { showCloseButton: true }) | null => {
  if (
    isMissing(bannerValues.alertTitleEn) ||
    isMissing(bannerValues.alertMessageEn) ||
    isMissing(bannerValues.alertSeverity)
  ) {
    return null
  }

  const rawSeverity = bannerValues.alertSeverity.toLowerCase()
  if (!isSeverity(rawSeverity)) return null
  const alertTitle = localize(bannerValues, 'alertTitle', language, '')
  const alertMessage = localize(bannerValues, 'alertMessage', language, '')

  const alertLink =
    bannerValues.alertLink && isSafeUrl(bannerValues.alertLink)
      ? bannerValues.alertLink
      : undefined

  let alertLinkText: string | undefined
  if (alertLink) {
    const rawText = localize(bannerValues, 'alertLinkText', language, '')
    alertLinkText = rawText && !isMissing(rawText) ? rawText : alertLink
  }

  return {
    alertTitle,
    alertMessage,
    alertSeverity: rawSeverity,
    ...(alertLink && { alertLink }),
    ...(alertLinkText && { alertLinkText }),
    showCloseButton: true,
    ariaLabel: 'Close disaster relief banner'
  }
}

export default getDisasterReliefBannerProps
